import React from "react";
import MultiSelect from "@/components/form/MultiSelect";
import { Label } from "@/components/form";

type Item = {
  [key: string]: any;
  selected?: boolean;
};

interface MultiSelectFieldProps {
  /**
   * Unique identifier for the field
   */
  id: string;
  
  /**
   * Name attribute for the field
   */
  name?: string;
  
  /**
   * Label displayed above the component
   */
  label: string;
  
  /**
   * Items to display in the dropdown
   */
  items: Item[];
  
  /**
   * Currently selected item IDs
   */
  value: string[];
  
  /**
   * Handler for when selected items change
   */
  onChange: (selected: string[]) => void;
  
  /**
   * Key to use as the item's unique identifier
   */
  itemKey?: string;
  
  /**
   * Key to use as the item's display text
   */
  textKey?: string;

  /**
   * Optional secondary text key — renders a smaller sub-line under each option in
   * the dropdown (e.g. a division label). Chips still show only `textKey`.
   */
  subTextKey?: string;

  /**
   * Placeholder text when no items are selected
   */
  placeholder?: string;
  
  /**
   * Whether the field is required
   */
  required?: boolean;
  
  /**
   * Error message or boolean indicating field has an error
   */
  error?: boolean | string;
  
  /**
   * Hint text displayed below the field
   */
  hint?: string;
  
  /**
   * Whether to enable grouping items by section
   */
  showSectionHeaders?: boolean;
  
  /**
   * Whether to show the list of selected items
   */
  showSelectedItems?: boolean;
  
  /**
   * Whether to allow clearing all selections at once
   */
  canClear?: boolean;
  
  /**
   * Key to use for section grouping
   */
  sectionKey?: string;
  
  /**
   * Additional CSS classes
   */
  className?: string;
  
  /**
   * Whether the component is disabled
   */
  disabled?: boolean;
}

const MultiSelectField: React.FC<MultiSelectFieldProps> = ({
  id,
  name,
  label,
  items,
  // Default to [] so callers that pass an undefined `value` (e.g. parent state
  // that hasn't been initialized yet, or a partial setState that drops the
  // field) don't crash on `.includes()` below. Matches the defensive
  // `value={x || []}` pattern already used by ClassForm and PrivateLessonForm
  // at their call sites.
  value = [],
  onChange,
  itemKey = "id",
  textKey = "text",
  subTextKey,
  placeholder,
  required = false,
  error = false,
  hint,
  showSectionHeaders = false,
  showSelectedItems = true,
  canClear = true,
  sectionKey,
  className = "",
  disabled = false,
}) => {
  // Map items to include selected state based on value prop
  const itemsWithSelection = items.map(item => ({
    ...item,
    selected: value.includes(item[itemKey])
  }));
  
  return (
    <div className={className}>
      <Label htmlFor={id} required={required}>
        {label}
      </Label>
      
      <MultiSelect
        items={itemsWithSelection}
        onChange={onChange}
        itemKey={itemKey}
        textKey={textKey}
        subTextKey={subTextKey}
        placeholder={placeholder}
        showSectionHeaders={showSectionHeaders}
        showSelectedItems={showSelectedItems}
        canClear={canClear}
        sectionKey={sectionKey}
        disabled={disabled}
      />
      
      {(typeof error === 'string' && error) || (hint && !error) ? (
        <p className={`mt-1.5 text-xs ${
          typeof error === 'string' ? "text-error-500" : "text-gray-500 dark:text-gray-400"
        }`}>
          {(typeof error === 'string' && error) || hint}
        </p>
      ) : null}
    </div>
  );
};

export default MultiSelectField; 